import { useEffect, useState } from 'react';
import type { CoreGroupLanguageSettings, CoreSiteLanguageSettings, VisitorLocale } from '@cloudsign/shared';
import { VisitorLocale as VisitorLocaleSchema } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { Button, Card, CheckboxField, SelectField, Spinner } from '../../components/ui';
import { useScopedSettings } from '../sites/api';
import type { SettingsCardProps } from '../../components/SettingsCards';
import { localeChoices } from './api';
import type { LocaleChoice } from './api';

const label = (c: LocaleChoice): string => (c.draft ? t('setup.lang.draftOption', { name: c.endonym }) : c.endonym);

function Footer({ canEdit, changed, pending, onDiscard, onSave }: { canEdit: boolean; changed: boolean; pending: boolean; onDiscard: () => void; onSave: () => void }) {
  if (!canEdit) return undefined;
  return (
    <>
      <Button disabled={!changed} onClick={onDiscard}>
        {t('settings.discard')}
      </Button>
      <Button variant="primary" disabled={!changed || pending} onClick={onSave}>
        {t('common.save')}
      </Button>
    </>
  );
}

/**
 * Site card "Languages" (languages.md s3): the default language and the ones offered in the picker. Drafts are listed only
 * where the portal build says the platform allows them (or when already saved, locked). Saves with the site settings
 * (module key `core`).
 */
export function SiteLanguagesCard({ scopeId, canEdit }: SettingsCardProps) {
  const settings = useScopedSettings(`/sites/${scopeId}/settings`, 'core');
  const saved = (settings.values as Partial<CoreSiteLanguageSettings> | undefined)?.languages;
  const [defaultLanguage, setDefault] = useState<VisitorLocale>('en-GB');
  const [offered, setOffered] = useState<VisitorLocale[]>([]);
  useEffect(() => {
    if (!saved) return;
    setDefault(saved.defaultLanguage ?? 'en-GB');
    setOffered(saved.offered ?? []);
  }, [saved]);
  if (settings.query.isError) return <ErrorPanel error={settings.query.error} onRetry={() => void settings.query.refetch()} />;
  if (!settings.values) return <Spinner />;
  const choices = localeChoices([...(saved?.offered ?? []), saved?.defaultLanguage ?? 'en-GB', defaultLanguage, ...offered]);
  // CloudSign is UK English only for now: with a single language on offer there is nothing to choose, so the card stays out of the way.
  if (choices.length < 2) return null;
  const changed = defaultLanguage !== (saved?.defaultLanguage ?? 'en-GB') || offered.join() !== (saved?.offered ?? []).join();
  const toggle = (code: VisitorLocale) => setOffered((o) => (o.includes(code) ? o.filter((x) => x !== code) : [...o, code]));
  return (
    <Card
      title={t('setup.lang.site.title')}
      description={t('setup.lang.site.description')}
      footer={
        <Footer
          canEdit={canEdit}
          changed={changed}
          pending={settings.save.isPending}
          onDiscard={() => {
            setDefault(saved?.defaultLanguage ?? 'en-GB');
            setOffered(saved?.offered ?? []);
          }}
          onSave={() => settings.save.mutate({ languages: { defaultLanguage, offered } })}
        />
      }
    >
      <div className="space-y-4">
        <ErrorLine error={settings.save.error} />
        <SelectField label={t('setup.lang.site.default')} hint={t('setup.lang.site.defaultHint')} value={defaultLanguage} disabled={!canEdit} onChange={(e) => setDefault(VisitorLocaleSchema.parse(e.target.value))}>
          {choices.map((c) => (
            <option key={c.code} value={c.code} disabled={c.locked && c.code !== defaultLanguage}>
              {label(c)}
            </option>
          ))}
        </SelectField>
        <fieldset className="space-y-2" disabled={!canEdit}>
          <legend className="mb-1 text-sm font-medium text-slate-800">{t('setup.lang.site.offered')}</legend>
          <p className="text-xs text-slate-600">{t('setup.lang.site.offeredHint')}</p>
          {choices.map((c) => (
            <CheckboxField key={c.code} label={label(c)} checked={offered.includes(c.code)} disabled={c.locked && !offered.includes(c.code)} onChange={() => toggle(c.code)} />
          ))}
        </fieldset>
      </div>
    </Card>
  );
}

/** Group card "Default language" (languages.md s3): null = follow the site. */
export function GroupLanguageCard({ scopeId, canEdit }: SettingsCardProps) {
  const settings = useScopedSettings(`/groups/${scopeId}/settings`, 'core');
  const saved = (settings.values as Partial<CoreGroupLanguageSettings> | undefined)?.defaultLanguage ?? null;
  const [value, setValue] = useState<VisitorLocale | null>(null);
  useEffect(() => setValue(saved), [saved]);
  if (settings.query.isError) return <ErrorPanel error={settings.query.error} onRetry={() => void settings.query.refetch()} />;
  if (!settings.values) return <Spinner />;
  const choices = localeChoices([...(saved ? [saved] : []), ...(value ? [value] : [])]);
  if (choices.length < 2) return null;
  return (
    <Card
      title={t('setup.lang.group.title')}
      description={t('setup.lang.group.description')}
      footer={<Footer canEdit={canEdit} changed={value !== saved} pending={settings.save.isPending} onDiscard={() => setValue(saved)} onSave={() => settings.save.mutate({ defaultLanguage: value })} />}
    >
      <div className="space-y-4">
        <ErrorLine error={settings.save.error} />
        <SelectField label={t('setup.lang.group.default')} hint={t('setup.lang.group.defaultHint')} value={value ?? ''} disabled={!canEdit} onChange={(e) => setValue(e.target.value === '' ? null : VisitorLocaleSchema.parse(e.target.value))}>
          <option value="">{t('setup.lang.group.followSite')}</option>
          {choices.map((c) => (
            <option key={c.code} value={c.code} disabled={c.locked && c.code !== value}>
              {label(c)}
            </option>
          ))}
        </SelectField>
      </div>
    </Card>
  );
}
